import { store } from '../state/store'
import type { BatteryOption, Chip, ConfirmCard, Driver, RowKey } from '../state/types'
import { SUPPORT_PHONE_DISPLAY } from '../config'

/**
 * Scripted demo driver — the design handoff's prototype conversation ported
 * verbatim. It is the acceptance harness for the visual layer: every orb
 * state, card rhythm and panel update the Realtime bridge must produce can be
 * watched here without any AI attached. Enable with ?demo=1 (+ ?auto=1).
 */
export class ScriptDriver implements Driver {
	private alive = true
	private timers = new Set<number>()
	private pending: { resolve: (label: string) => void; chips: Chip[]; show: boolean } | null = null
	private editing = false
	private draftShown = false
	private auto: boolean
	private slot = ''

	constructor(auto = false) {
		this.auto = auto
	}

	dispose(): void {
		this.alive = false
		this.timers.forEach((t) => window.clearTimeout(t))
		store.stopHold()
	}

	private st(fn: () => void, ms: number): number {
		const id = window.setTimeout(() => {
			this.timers.delete(id)
			if (this.alive) fn()
		}, ms)
		this.timers.add(id)
		return id
	}

	private wait(ms: number): Promise<void> {
		return new Promise((r) => this.st(r, ms))
	}

	/* Timing model per handoff: ~480ms think, 700ms + 16ms/char speak (cap 2.4s). */
	private async say(text: string): Promise<void> {
		store.setOrb('thinking')
		await this.wait(480)
		store.setLine(text)
		store.set({ userEcho: '', orb: 'speaking' })
		await this.wait(Math.min(2400, 700 + text.length * 16))
		if (store.get().orb === 'speaking') store.setOrb('idle')
	}

	private ask(labels: (string | { label: string; primary?: boolean })[], show = true): Promise<string> {
		return new Promise((res) => {
			const chips: Chip[] = labels.map((l, i) => {
				const o = typeof l === 'string' ? { label: l } : l
				return { id: `c${i}`, label: o.label, primary: o.primary }
			})
			this.pending = { resolve: res, chips, show }
			store.set({ chips: show ? chips : [], statusHint: show ? 'none' : 'dictate' })
			if (this.auto) {
				this.st(() => {
					if (this.pending && this.pending.resolve === res) this.resolvePick(chips[0].label)
				}, 1700)
			}
		})
	}

	private resolvePick(label: string): void {
		if (!this.pending) return
		const p = this.pending
		this.pending = null
		this.editing = false
		this.keepHandler = null
		store.setEcho(label)
		store.set({ chips: [], statusHint: 'none', listening: false, orb: 'listening' })
		this.st(() => {
			if (store.get().orb === 'listening') store.setOrb('thinking')
		}, 600)
		p.resolve(label)
	}

	private async showCard(
		key: RowKey | null,
		label: string,
		value: string,
		opts: Partial<ConfirmCard> & { holdMs?: number } = {},
	): Promise<void> {
		const card: ConfirmCard = { label, value, note: opts.note, noteGreen: opts.noteGreen, green: opts.green }
		if (key) store.set({ sum: { ...store.get().sum, [key]: value } })
		store.set({ card })
		await this.wait(opts.holdMs || 2700)
		store.set({ card: { ...card, leaving: true } })
		await this.wait(300)
		store.set({ card: null })
	}

	private askOptions(opts: Omit<BatteryOption, 'id' | 'state'>[]): Promise<BatteryOption> {
		return new Promise((res) => {
			let locked = false
			const built: BatteryOption[] = opts.map((o, i) => ({ ...o, id: `bat-${i}`, state: 'normal' }))
			const pick = (chosen: BatteryOption) => {
				if (locked) return
				locked = true
				this.pending = null
				const shown = built.map((x) =>
					x.id === chosen.id ? { ...x, state: 'chosen' as const } : { ...x, state: 'dimmed' as const },
				)
				store.setEcho(`A(z) ${chosen.short} legyen.`)
				store.set({ chips: [], options: shown, orb: 'thinking' })
				this.st(() => {
					store.set({ options: null })
					res(chosen)
				}, 1300)
			}
			this.optionPick = pick
			store.set({ options: built })
			const chips: Chip[] = built.map((o) => ({ id: o.id, label: o.short, primary: !!o.tag }))
			this.pending = {
				resolve: (l: string) => {
					const f = built.find((o) => o.short === l)
					pick(f || built[1])
				},
				chips,
				show: false,
			}
			if (this.auto) this.st(() => { if (!locked) pick(built[1]) }, 2200)
		})
	}

	private optionPick: ((o: BatteryOption) => void) | null = null

	/* ---------------- Driver interface ---------------- */

	async start(withMic: boolean): Promise<void> {
		// The demo never touches a microphone; entering through „Inkább írok"
		// only lands on the same typed status hint the Realtime bridge shows.
		if (!withMic) store.set({ statusHint: 'dictate' })
		void this.run()
	}

	pickChip(chip: Chip): void {
		if (chip.id === 'keep' && this.keepHandler) {
			this.keepHandler()
			return
		}
		this.resolvePick(chip.label)
	}

	pickOption(opt: BatteryOption): void {
		this.optionPick?.(opt)
	}

	submitText(text: string): void {
		if (this.pending) this.resolvePick(text)
	}

	toggleMic(): void {
		const s = store.get()
		if (s.listening) {
			store.set({ listening: false, orb: 'idle' })
			return
		}
		store.set({ listening: true, orb: 'listening' })
		this.st(() => {
			const cur = store.get()
			if (cur.listening && this.pending) this.resolvePick(this.pending.chips[0].label)
			else if (cur.listening) store.set({ listening: false, orb: 'idle' })
		}, 2300)
	}

	/* The original pending question stays live during an edit — „Hagyjuk így"
	   only restores the saved chips, it never resolves the original promise. */
	editRow(key: RowKey, label: string): void {
		if (!key || store.get().done || !this.pending || this.editing) return
		this.editing = true
		const saved = this.pending.chips
		const savedShow = this.pending.show
		store.setEcho(`A(z) „${label.toLowerCase()}" adatát módosítanám.`)
		store.set({ chips: [], statusHint: 'none', expanded: false })
		void this.say('Rendben. Mondja az új adatot — vagy hagyjuk így?').then(() => {
			this.keepHandler = () => {
				this.keepHandler = null
				store.setEcho('Hagyjuk így.')
				store.set({ chips: [] })
				void this.say('Rendben, változatlanul hagytam.').then(() => {
					this.editing = false
					store.set({ chips: savedShow ? saved : [], statusHint: savedShow ? 'none' : 'dictate' })
				})
			}
			store.set({ chips: [{ id: 'keep', label: 'Hagyjuk így' }] })
		})
	}

	private keepHandler: (() => void) | null = null

	callTapped(): void {
		if (this.draftShown || store.get().done) return
		this.draftShown = true
		store.set({ draft: { code: '4F7K', phone: SUPPORT_PHONE_DISPLAY } })
		this.st(() => {
			store.set({ draft: null })
			this.draftShown = false
		}, 9000)
	}

	/* ---------------- The reference conversation ---------------- */

	private async run(): Promise<void> {
		await this.wait(900)
		await this.say('Üdvözlöm! Melyik autóba keresünk akkumulátort? Mondja a márkát és a típust.')
		let r = await this.ask(['Opel Astra, benzines', 'Nem tudom pontosan'], false)
		if (r === 'Nem tudom pontosan') {
			await this.say('Semmi gond, lépésenként megyünk. Milyen márkájú az autó?')
			await this.ask(['Opel'], false)
			await this.say('És a típusa? Ha nem biztos benne, az évjárat is segít.')
			await this.ask(['Astra, kb. 2012-es'], false)
		}
		await this.say('Megvan: Opel Astra. Melyik motor van benne?')
		r = await this.ask(['1.6 benzin', '1.7 dízel', 'Nem tudom'])
		if (r === 'Nem tudom') {
			await this.say('Akkor az évjárat alapján szűkítem: 2009 és 2015 között gyártották?')
			await this.ask(['Igen, 2012-es'])
			r = '1.6 benzin'
		}
		const carName = r === '1.7 dízel' ? 'Opel Astra J 1.7 CDTI (2009–2015)' : 'Opel Astra J 1.6 benzin (2009–2015)'
		await this.showCard('car', 'Az Ön autója', carName, { note: '63 575 jármű adatbázisából azonosítva' })
		await this.say('Három akkumulátor illik pontosan ehhez az autóhoz. A Bären Blu Polart ajánlom: erős hidegindítás, 3 év garancia.')
		const picked = await this.askOptions([
			{ name: 'Top Akku', short: 'Top Akku 55Ah', spec: '55Ah · 480A · 2 év garancia', price: '38 900 Ft', reason: 'Kedvező árú, megbízható belépő szint.', img: '/demo/bat-top.jpg' },
			{ name: 'Bären Blu Polar', short: 'Blu Polar 64Ah', spec: '64Ah · 610A · 3 év garancia', price: '42 900 Ft', tag: 'Ajánlott', reason: 'Bevált választás ehhez a motorhoz — a legjobb ár-érték arány.', img: '/demo/bat-blu.jpg' },
			{ name: 'Bären Polar Technik AGM', short: 'Polar Technik 70Ah', spec: '70Ah · 760A · 4 év garancia · AGM', price: '54 900 Ft', reason: 'Prémium élettartam, sok elektromos fogyasztó esetén éri meg.', img: '/demo/bat-agm.jpg' },
		])
		store.set({ total: '57 400 Ft' })
		await this.showCard('bat', 'Választott akkumulátor', `${picked.name} — ${picked.price}`, {
			green: true,
			note: `Garantáltan megfelel az Ön autójához · ${carName}`,
			noteGreen: true,
			holdMs: 3200,
		})
		await this.say('A végösszeg a kiszállással és beszereléssel együtt 57 400 Ft — fent végig látja. Hová menjünk? Mondja a címet.')
		r = await this.ask(['1113 Budapest, Bocskai út 77.', 'Diktálom a címet'], false)
		if (r === 'Diktálom a címet') {
			await this.say('Hallgatom a címet.')
			await this.ask(['Bocskai út 77., tizenegyedik kerület'], false)
		}
		await this.showCard('addr', 'Helyszín', '1113 Budapest, Bocskai út 77.', { note: 'Kiszállási zónán belül — a szerelő Önhöz megy' })
		await this.say('Mikor jó Önnek? A legközelebbi szabad időpontjaink:')
		r = await this.ask(['Ma 17:00–18:00', 'Holnap 14:00–15:00', 'Péntek 9:00–10:00'])
		const slotMap: Record<string, string> = {
			'Ma 17:00–18:00': 'ma, aug. 6., 17:00–18:00',
			'Holnap 14:00–15:00': 'csütörtök, aug. 7., 14:00–15:00',
			'Péntek 9:00–10:00': 'péntek, aug. 8., 9:00–10:00',
		}
		this.slot = slotMap[r] || 'csütörtök, aug. 7., 14:00–15:00'
		store.startHold()
		await this.showCard('slot', 'Időpont', this.slot, { note: 'Ezt az időpontot lefoglaltuk Önnek.', noteGreen: true, holdMs: 3000 })
		await this.say('Milyen néven és telefonszámon érhetjük el?')
		r = await this.ask(['Kovács Anna · +36 30 123 4567', 'Diktálom'], false)
		if (r === 'Diktálom') {
			await this.say('Hallgatom: név és telefonszám.')
			await this.ask(['Kovács Anna, +36 30 123 4567'], false)
		}
		await this.say('Visszaolvasom a számot: +36 · 30 · 123 · 45 · 67. Helyes?')
		r = await this.ask(['Igen, helyes', 'Nem, javítom'])
		if (r === 'Nem, javítom') {
			await this.say('Mondja újra, kérem, számjegyenként.')
			await this.ask(['+36 30 123 4567'], false)
			await this.say('Köszönöm, javítottam.')
		}
		await this.showCard('contact', 'Kapcsolat', 'Kovács Anna · +36 30 123 4567')
		await this.say('Fizetni a helyszínen tud, a beszerelés után. Készpénzzel vagy kártyával?')
		r = await this.ask(['Készpénzzel', 'Kártyával'])
		await this.showCard('pay', 'Fizetés', `${r === 'Kártyával' ? 'Kártyával' : 'Készpénzben'}, a helyszínen`)
		store.set({
			price: {
				total: '57 400 Ft',
				lines: [
					{ k: `${picked.name} akkumulátor`, v: picked.price },
					{ k: 'Kiszállás és beszerelés', v: '14 500 Ft' },
					{ k: 'Régi akku elszállítása', v: '0 Ft' },
				],
			},
		})
		await this.say('Minden pontot közösen egyeztettünk — kérjük, ellenőrizze. Mehet a rendelés?')
		r = await this.ask([{ label: 'Megrendelem · 57 400 Ft', primary: true }, 'Még módosítanék'])
		if (r === 'Még módosítanék') {
			await this.say('Rendben. Koppintson fent arra a pontra, amit módosítana — vagy mondja el.')
			await this.ask([{ label: 'Mégis megrendelem · 57 400 Ft', primary: true }])
		}
		store.stopHold()
		const price = store.get().price
		store.set({ done: true, chips: [], holdOn: false, price: price ? { ...price, leaving: true } : null })
		await this.wait(320)
		store.set({ price: null, success: { orderNo: 'MO11417', slot: this.slot }, assistantLine: '', userEcho: '' })
		await this.say('Köszönjük a rendelést! Ha bármi kérdése van, hívjon minket bátran.')
	}
}
